import { TableRow, TableCell, Typography, IconButton, Stack, StackColumn, Box } from '@dhl-ui/components'
import { DuplicateIcon } from '@dhl-ui/icons'
import { useClipboard } from '~/common'

type Props = {
  // the title of the row, will be shown in the first column
  title: string
  // the payload of the row, will be shown in the second column
  payload: string | number | undefined
  // whether to show a copy button in the second column
  withCopy?: boolean
  // whether to show the payload in a multiline text field
  multiline?: boolean
  // whether to always show the row, even if the payload is undefined or an empty string
  alwaysShowEmpty?: boolean
}

export const DataRow = ({
  title,
  payload = '',
  withCopy = false,
  multiline = false,
  alwaysShowEmpty = false,
}: Props) => {
  const [hasClipboard, copyToClipboard] = useClipboard()
  // != null is used here to check for both null and undefined, because both are considered "empty" values in this context.
  const copyEnabled = withCopy && hasClipboard && payload != null && String(payload).length > 0

  // Exit early when nothing has to be displayed, e.g. when payload is undefined or an empty string.
  // This doesn't apply to numbers, because 0 is a valid value that should be displayed.
  if (!alwaysShowEmpty && typeof payload !== 'number' && !payload) return null
  const displayValue = String(payload)

  return (
    <TableRow>
      <TableCell>
        <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" alignContent="center" alignItems="center">
          <StackColumn>
            {/* multiline content is rendered in a box with `pre-wrap` enabled, so it shows the actual lines. */}
            {multiline ? (
              <Box
                sx={{
                  width: '80ch',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {displayValue}
              </Box>
            ) : (
              <Typography>{displayValue}</Typography>
            )}
          </StackColumn>
          {copyEnabled && (
            <StackColumn>
              <IconButton
                aria-label="copy text"
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(displayValue)}
              >
                <DuplicateIcon />
              </IconButton>
            </StackColumn>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}
